import { m as motion, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Page } from '@/entities/page';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getAccordions } from '@/shared/api/accordions';
import { getDays } from '@/shared/api/days';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getReasons } from '@/shared/api/reasons';
import { getSelectionStages } from '@/shared/api/selection-stages';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import ApplyRegistrationPopupCombined from '@/shared/components/popups/ApplyRegistrationPopupСombined';
import AboutSection from '@/shared/components/sections/AboutSection';
import { AboutSectionProps } from '@/shared/components/sections/AboutSection/AboutSection';
import AwardsSection, { AwardsSectionProps } from '@/shared/components/sections/AwardsSection';
import MissionSection, { MissionSectionProps } from '@/shared/components/sections/MissionSection';
import OrganizeSection, { OrganizeSectionProps } from '@/shared/components/sections/OrganizeSection';
import PageWelcomeDynamicBG from '@/shared/components/sections/PageWelcomeDynamicBG';
import { PageWelcomeDynamicBGProps } from '@/shared/components/sections/PageWelcomeDynamicBG/PageWelcomeDynamicBG';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import SelectionStagesSection, {
    SelectionStagesSectionProps,
} from '@/shared/components/sections/SelectionStagesSection';
import TextWithImageSection, {
    TextWithImageSectionProps,
} from '@/shared/components/sections/TextWithImageSection/TextWithImageSection';
import ThemedTracksSection from '@/shared/components/sections/ThemedTracksSection';
import Button from '@/shared/components/shared/Button';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import NavigationSections from '@/shared/components/shared/NavigationSections';
import Typografed from '@/shared/components/shared/Typografed';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { computeEventDatesFromDays, generateEventPageJsonLd } from '@/shared/lib/utils/event-page-json-ld';
import { ThemedTracksSectionProps } from '../../shared/components/sections/ThemedTracksSection/ThemedTracksSection';

const PREMIYA_TEC_SECTION_IDS = {
    about: 'aboutSection',
    whoCan: 'who-can',
    textWithImage: 'textWithImage',
    awards: 'adwards',
    selectionStages: 'selectionStages',
    organize: 'organize',
    feedback: 'feedback',
    themedTracks: 'themedTracks',
    nominations: 'nominations',
} as const;

const PremiyaTec = ({
    meta,
    footerData,
    breadcrumbs,
    page,
    h1,
    navigation,
    bodyClass,
    anchors,
    sectionIds,
    aboutSectionProps,
    organizeSectionProps,
    textWithImageSectionProps,
    selectionStagesSectionProps,
    contactPersonProps,
    questionsSectionProps,
    missionSectionProps,
    AwardsSectionProps,
    eventStartDateIso,
    eventEndDateIso,
    themedTracksSectionProps,
    pageWelcomeDynamicBGProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });

    return (
        <DefaultLayout currentPageId={PAGES.HR} bodyClass={bodyClass}>
            <section className="premiya-tec__section" ref={ref}>
                <PageWelcomeDynamicBG
                    breadcrumbs={breadcrumbs}
                    pageWelcomeDynamicBGProps={pageWelcomeDynamicBGProps}
                    className="breadcrumb-dark-bg"
                    data-theme="light"
                />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <NavigationAnchors
                        theme={theme}
                        navigation={navigation}
                        anchors={anchors}
                        scrollYProgress={scrollYProgress}
                    />

                    {aboutSectionProps.title && aboutSectionProps.description && (
                        <AboutSection
                            aboutSectionProps={aboutSectionProps}
                            document={page?.file}
                            data-theme="light-bg"
                            id={sectionIds.about}
                        />
                    )}

                    <MissionSection
                        data-theme="light-bg"
                        id={sectionIds.whoCan}
                        missionSectionProps={missionSectionProps}
                    />

                    <TextWithImageSection
                        textWithImageSectionProps={textWithImageSectionProps}
                        id={sectionIds.textWithImage}
                        data-theme="light-bg"
                    >
                        <div className="summit__text-with-image__content">
                            {textWithImageSectionProps.text && (
                                <div
                                    className="text-with-image__text text-m "
                                    dangerouslySetInnerHTML={{ __html: tp(textWithImageSectionProps.text) }}
                                ></div>
                            )}
                            {textWithImageSectionProps.description && (
                                <div className="text-with-image__block-bottom">
                                    <div
                                        className="text-with-image__description wysiwyg text-s"
                                        dangerouslySetInnerHTML={{
                                            __html: tp(textWithImageSectionProps.description),
                                        }}
                                    ></div>
                                </div>
                            )}

                            {textWithImageSectionProps.link && textWithImageSectionProps.link.href && (
                                <Button
                                    tag={Link}
                                    href={textWithImageSectionProps.link.href}
                                    variant="outline-dark"
                                    className="selection-stages-card-btn"
                                >
                                    <Typografed>{textWithImageSectionProps.link.name}</Typografed>
                                </Button>
                            )}
                        </div>
                    </TextWithImageSection>

                    <ThemedTracksSection
                        id={sectionIds.themedTracks}
                        data-theme="light-bg"
                        themedTracksSectionProps={themedTracksSectionProps}
                    />

                    {AwardsSectionProps && AwardsSectionProps.awards.length > 0 && (
                        <AwardsSection
                            id={sectionIds.awards}
                            awardsSectionProps={AwardsSectionProps}
                            data-theme="light-bg"
                        />
                    )}

                    {selectionStagesSectionProps.cards && selectionStagesSectionProps.cards.length > 0 && (
                        <SelectionStagesSection
                            id={sectionIds.selectionStages}
                            data-theme="light-bg"
                            selectionStagesSectionProps={selectionStagesSectionProps}
                        />
                    )}
                    {organizeSectionProps.categories.length > 0 && (
                        <OrganizeSection
                            id={sectionIds.organize}
                            data-theme="light-bg"
                            organizeSectionProps={organizeSectionProps}
                        />
                    )}

                    <QuestionsSection
                        id={sectionIds.feedback}
                        data-theme="light-bg"
                        questionsSectionProps={questionsSectionProps}
                        contactPersonProps={contactPersonProps}
                        className="js-header-theme-trigger"
                    />
                </motion.div>
            </section>
            <ApplyRegistrationPopupCombined />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-premiya-tec"
                data={generateEventPageJsonLd({
                    name: pageWelcomeDynamicBGProps.title || h1,
                    descriptionHtml: aboutSectionProps.description,
                    metaDescription: meta.description,
                    pagePath: '/events/premiya-tec/',
                    imageSrc: textWithImageSectionProps.img?.src ?? null,
                    startDateIso: eventStartDateIso ?? undefined,
                    endDateIso: eventEndDateIso ?? undefined,
                    breadcrumbs,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default PremiyaTec;

type PremiyaTecPageProps = NonUndefined<
    CommonPageProps & {
        anchors: Anchors;
        sectionIds: typeof PREMIYA_TEC_SECTION_IDS;
        eventStartDateIso: string | null;
        eventEndDateIso: string | null;
        page?: Page;
        h1: string;
        aboutSectionProps: AboutSectionProps;
        organizeSectionProps: OrganizeSectionProps;
        textWithImageSectionProps: TextWithImageSectionProps;
        missionSectionProps: MissionSectionProps;
        selectionStagesSectionProps: SelectionStagesSectionProps;
        navigation: AppNavigation;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        AwardsSectionProps: AwardsSectionProps;
        themedTracksSectionProps: ThemedTracksSectionProps;
        pageWelcomeDynamicBGProps: PageWelcomeDynamicBGProps;
    }
>;

export const getStaticProps: GetStaticProps<PremiyaTecPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'premiya-tec' } });

    const companyCategoriesResponse = await getCompanyCategories({
        params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC },
    });
    const [
        commonPageProps,
        companiesResponse,
        selectionStagesResponse,
        employeesResponse,
        reasonsResponse,
        accordionsResponse,
        daysResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.PREMIYA_TEC),
        companyCategoriesResponse.data?.length === 0
            ? Promise.resolve<AppCompaniesResponse>({ status: 'success', data: [] })
            : getCompanies({
                  params: { categories: (companyCategoriesResponse.data ?? []).map((category) => category.id) },
              }),
        getSelectionStages({ params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC } }),
        getReasons({ params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC } }),
        getAccordions({ params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC } }),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.PREMIYA_TEC } }),
        getSiteSettings({
            params: {
                keys: [
                    'premiya_tec_main_title',
                    'premiya_tec_main_dates',
                    'premiya_tec_main_description',

                    'premiya_tec_first_screen_title',
                    'premiya_tec_first_screen_description',

                    'premiya_tec_company_title',

                    'premiya_tec_about_title',
                    'premiya_tec_about_subtitle',
                    'premiya_tec_about_description',
                    'premiya_tec_about_image',

                    'premiya_tec_themed_tracks_title',
                    'premiya_tec_themed_tracks_description',
                    'premiya_tec_themed_tracks_application_link',
                    'premiya_tec_themed_tracks_numbers',
                    'premiya_tec_themed_tracks_themed_tracks',

                    'premiya_tec_nominations_title',

                    'premiya_tec_selection_stages_title',
                    'premiya_tec_selection_stages_documents',

                    'premiya_tec_form_title',
                    'premiya_tec_form_description',

                    'premiya_tec_form_link_text',
                    'premiya_tec_form_link',

                    'premiya_tec_participate_title',
                    'premiya_tec_participate_button_text',
                    'premiya_tec_participate_button_link',

                    'premiya_tec_about_link',
                    'premiya_tec_about_link_text',
                ],
            },
        }),
    ]);

    const premiyaAnchors: Anchors = [
        ...((siteSettings.data?.premiya_tec_first_screen_title ?? 'О Премии ТЭК') &&
        (siteSettings.data?.premiya_tec_first_screen_description ?? '')
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.about,
                      name: siteSettings.data?.premiya_tec_first_screen_title ?? 'О премии',
                  },
              ]
            : []),
        ...((reasonsResponse.data ?? []).length > 0
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.whoCan,
                      name: 'Зачем участвовать',
                  },
              ]
            : []),
        ...(siteSettings.data?.premiya_tec_themed_tracks_title &&
        siteSettings.data?.premiya_tec_themed_tracks_description
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.themedTracks,
                      name: 'Тематические треки',
                  },
              ]
            : []),

        ...((accordionsResponse.data?.filter((item) => item.blockNumber == 1) ?? []).length > 0
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.awards,
                      name: 'Номинации',
                  },
              ]
            : []),
        ...((selectionStagesResponse.data ?? []).length > 0
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.selectionStages,
                      name: 'Этапы отбора',
                  },
              ]
            : []),
        ...((companyCategoriesResponse.data ?? []).length > 0
            ? [
                  {
                      id: PREMIYA_TEC_SECTION_IDS.organize,
                      name: 'Организаторы и партнеры',
                  },
              ]
            : []),
        {
            id: PREMIYA_TEC_SECTION_IDS.feedback,
            name: 'Обратная связь',
        },
    ];
    const { startDateIso: eventStartDateIso, endDateIso: eventEndDateIso } = computeEventDatesFromDays(
        daysResponse.data ?? [],
    );

    return {
        props: {
            ...commonPageProps,
            eventStartDateIso: eventStartDateIso ?? null,
            eventEndDateIso: eventEndDateIso ?? null,
            bodyClass: 'premiya-tec',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'События форума', href: '/events/summit/' },
                { text: 'Премия Территория технологий', href: '/events/premiya-tec/' },
            ],
            page: pageResponse.data,
            h1: 'События форума',

            navigation: [
                {
                    id: 0,
                    title: 'Премия Территория технологий',
                    href: '/events/premiya-tec/',
                },
                {
                    id: 1,
                    title: 'Технологические дни',
                    href: '/events/technological/',
                },
                {
                    id: 2,
                    title: 'HR-Саммит и Премия',
                    href: '/events/summit/',
                },
                {
                    id: 3,
                    title: 'TNF LIVE',
                    href: '/events/tnf-live/',
                },
            ],
            anchors: premiyaAnchors,
            sectionIds: PREMIYA_TEC_SECTION_IDS,

            pageWelcomeDynamicBGProps: {
                title: siteSettings.data?.premiya_tec_main_title ?? '',
                description: siteSettings.data?.premiya_tec_main_description ?? '',
                events:
                    siteSettings.data?.premiya_tec_main_dates?.map((date) => ({
                        text: date?.date ?? '',
                        title: date?.caption ?? '',
                    })) ?? [],
            },
            themedTracksSectionProps: {
                title: siteSettings.data?.premiya_tec_themed_tracks_title ?? '',
                description: siteSettings.data?.premiya_tec_themed_tracks_description ?? '',
                tracks:
                    siteSettings.data?.premiya_tec_themed_tracks_themed_tracks?.map((track) => ({
                        title: track?.title ?? '',
                        image: track?.image ?? { src: '', alt: '' },
                    })) ?? [],
                stats:
                    siteSettings.data?.premiya_tec_themed_tracks_numbers?.map((stat) => ({
                        value: stat?.value ?? '',
                        label: stat?.label ?? '',
                    })) ?? [],
                link: {
                    name: 'Подать заяку на участие',
                    href: siteSettings.data?.premiya_tec_themed_tracks_application_link ?? '',
                },
            },
            aboutSectionProps: {
                title: siteSettings.data?.premiya_tec_first_screen_title ?? 'О Премии ТЭК',
                description: siteSettings.data?.premiya_tec_first_screen_description ?? '',
            },

            organizeSectionProps: {
                title: siteSettings.data?.premiya_tec_company_title ?? 'Организаторы и партнеры',
                categories: (companyCategoriesResponse.data ?? []).map((category) => ({
                    title: category.name,
                    description: category.description ?? '',
                    items: (companiesResponse.data ?? [])
                        .filter((company) => company.categoryId === category.id)
                        .map((company) => ({
                            logo: company.logo,
                            title: company.title,
                            description: company.description ?? '',
                            href: company.href ?? '',
                            categoryId: company.categoryId,
                        })),
                })),
            },
            textWithImageSectionProps: {
                title: siteSettings.data?.premiya_tec_about_title ?? 'test',
                text:
                    siteSettings.data?.premiya_tec_about_description ??
                    `
                test
                `,
                description: siteSettings.data?.premiya_tec_about_subtitle ?? 'test',
                img: siteSettings.data?.premiya_tec_about_image ?? null,
                link: siteSettings.data?.premiya_tec_about_link
                    ? {
                          href: siteSettings.data?.premiya_tec_about_link ?? '',
                          name: siteSettings.data?.premiya_tec_about_link_text ?? 'Принять участие',
                      }
                    : null,
            },

            selectionStagesSectionProps: {
                title: siteSettings.data?.premiya_tec_selection_stages_title ?? 'test',
                documents: (siteSettings.data?.premiya_tec_selection_stages_documents ?? []).map((document) => ({
                    src: document.src,
                    title: document.title ?? '',
                    alt: document.alt ?? '',
                })),
                cards:
                    selectionStagesResponse.data?.map((item) => {
                        // TODO потом нормально задинамить
                        return {
                            ...item,
                            buttonText: item.buttonType === 'result' ? 'Подать проект' : item.buttonText,
                        };
                    }) ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.premiya_tec_form_title ?? 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.premiya_tec_form_description ?? '',
                linkText: siteSettings.data?.premiya_tec_form_link_text ?? 'Служба поддержки',
                href: siteSettings.data?.premiya_tec_form_link ?? 'https://t.me/tnf_oilgasforum_bot',
            },
            missionSectionProps: {
                title: siteSettings.data?.premiya_tec_participate_title ?? 'Номинации',
                subtitle:
                    siteSettings.data?.premiya_tec_participate_description ??
                    'Получите признание. Станьте лучшими в отрасли',
                link: {
                    href: siteSettings.data?.premiya_tec_participate_button_link ?? '',
                    name: siteSettings.data?.premiya_tec_participate_button_text ?? 'Подать заявку',
                },
                cards: reasonsResponse.data ?? [],
            },
            AwardsSectionProps: {
                title: siteSettings.data?.premiya_tec_nominations_title ?? 'Кто может участвовать',
                awards: accordionsResponse.data ? accordionsResponse.data.filter((item) => item.blockNumber == 1) : [],
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
        } satisfies PremiyaTecPageProps,
        revalidate: 60,
    };
};
